import { Injectable, computed, signal } from '@angular/core';
import { StorageService } from '@core/services/storage.service';

export type ProjectTaskColumn = 'priority' | 'minutes' | 'deadline' | 'comment';

const COLUMN_WIDTHS: Record<ProjectTaskColumn, string> = {
  priority: '120px',
  minutes: '90px',
  deadline: '155px',
  comment: 'minmax(0, 1fr)'
};

const DEFAULT_COLUMNS: Record<ProjectTaskColumn, boolean> = {
  priority: true,
  minutes: true,
  deadline: true,
  comment: true
};

@Injectable({ providedIn: 'root' })
export class ProjectTaskColumnsService {
  readonly columns: ProjectTaskColumn[] = ['priority', 'minutes', 'deadline', 'comment'];
  private readonly visibility = signal<Record<ProjectTaskColumn, boolean>>({ ...DEFAULT_COLUMNS });
  private readonly storageKey: string | null;

  readonly hasVisibleColumns = computed(() => this.columns.some((column) => this.visibility()[column]));
  readonly gridTemplateColumns = computed(() =>
    this.columns
      .filter((column) => this.visibility()[column])
      .map((column) => COLUMN_WIDTHS[column])
      .join(' ')
  );

  constructor(private readonly storage: StorageService) {
    const userId = this.storage.UserPerson?.id ?? this.storage.Employee?.userPerson?.id;
    this.storageKey = userId ? `projectTaskCardColumns:${userId}` : null;

    if (this.storageKey) {
      try {
        const saved = JSON.parse(this.storage.get(this.storageKey) || '{}');
        if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
          this.visibility.set(
            this.columns.reduce((result, column) => {
              result[column] = typeof saved[column] === 'boolean' ? saved[column] : true;
              return result;
            }, {} as Record<ProjectTaskColumn, boolean>)
          );
        }
      } catch {
        // Invalid browser state falls back to the default layout.
      }
    }
  }

  isVisible(column: ProjectTaskColumn): boolean {
    return this.visibility()[column];
  }

  toggle(column: ProjectTaskColumn): void {
    this.visibility.update((current) => ({ ...current, [column]: !current[column] }));
    if (this.storageKey) {
      this.storage.put(this.storageKey, JSON.stringify(this.visibility()));
    }
  }
}
